/**
 * Moteur audio (§8, E§4).
 *
 * Toute la politique — gamme, variation de hauteur, limiteur, escalade du carillon —
 * vit dans `core/soundPolicy.ts`, sans dépendance ni navigateur. Ce fichier n'est que
 * la couche Howler qui obéit.
 *
 * Trois règles non négociables sont câblées ici :
 *
 *  - **aucun son avant la première interaction** : les navigateurs bloquent l'autoplay,
 *    et un contexte audio créé trop tôt reste suspendu sans le dire ;
 *  - **musique coupée par défaut** (§8), volume séparé des effets ;
 *  - **un son manquant ne casse rien** : le jeu doit rester jouable si un fichier n'a
 *    pas encore été chargé.
 */

import { Howl, Howler } from 'howler';

import type { MusicHandle } from './music';
import { createMusic } from './music';

import { ChimeLadder, SoundLimiter, jitterRate, semitoneToRate } from '@/core/soundPolicy';

/** Effets du MVP (§8). Ajouter une entrée ici oblige à fournir l'échantillon. */
export const SFX_NAMES = [
  'purchase',
  'chime',
  'coin',
  'boost',
  'landExpand',
  'prestige',
  'milestone',
  'refused',
] as const;

export type SfxName = (typeof SFX_NAMES)[number];

/**
 * Échantillons du MVP — pack « Interface Sounds » de Kenney, CC0 (voir CREDITS.md).
 *
 * `purchase` et `chime` sont deux sons distincts **joués ensemble** à la pose : E§1.1
 * demande « un impact grave + un petit carillon aigu superposé ». L'impact reste fixe,
 * seul le carillon monte d'un demi-ton — c'est cette dissociation qui fait qu'une série
 * de poses joue une gamme sans que la matière du choc ne change.
 */
export const SFX_SOURCES: Readonly<Record<SfxName, string>> = {
  purchase: '/sounds/purchase.ogg',
  chime: '/sounds/chime.ogg',
  coin: '/sounds/coin.ogg',
  boost: '/sounds/boost.ogg',
  landExpand: '/sounds/land-expand.ogg',
  prestige: '/sounds/prestige.ogg',
  milestone: '/sounds/milestone.ogg',
  refused: '/sounds/refused.ogg',
};

export interface SoundSettings {
  readonly sfxVolume: number;
  readonly musicVolume: number;
  readonly musicEnabled: boolean;
}

/** Musique coupée par défaut, comme l'impose le §8. */
export const DEFAULT_SETTINGS: SoundSettings = {
  sfxVolume: 0.8,
  musicVolume: 0.5,
  musicEnabled: false,
};

const STORAGE_KEY = 'villopia.sound.v1';

export interface SfxOptions {
  /** Décalage en demi-tons, pour les sons qui montent (carillon de pose). */
  readonly semitones?: number;
  /** Multiplicateur de volume ponctuel. */
  readonly gain?: number;
}

export class SoundEngine {
  private readonly samples = new Map<SfxName, Howl>();
  private readonly limiter = new SoundLimiter();
  private readonly chime = new ChimeLadder();
  private music: MusicHandle | null = null;
  private settings: SoundSettings = DEFAULT_SETTINGS;
  private unlocked = false;

  constructor() {
    this.settings = readSettings();
  }

  getSettings(): SoundSettings {
    return this.settings;
  }

  /**
   * Débloque le contexte audio. À appeler depuis un vrai geste utilisateur —
   * un `pointerdown`, pas un `load`. Sans cela le contexte reste suspendu et tous
   * les sons partent dans le vide, silencieusement.
   */
  unlock(): void {
    if (this.unlocked) return;
    this.unlocked = true;
    Howler.volume(1);

    if (this.settings.musicEnabled) {
      this.startMusic();
    }
  }

  isUnlocked(): boolean {
    return this.unlocked;
  }

  /** Déclare un échantillon. Le jeu reste jouable tant qu'il n'est pas déclaré. */
  register(name: SfxName, sources: readonly string[]): void {
    this.samples.set(
      name,
      new Howl({ src: [...sources], preload: true, volume: this.settings.sfxVolume }),
    );
  }

  /**
   * Allume la musique GÉNÉRATIVE (ROADMAP 12.9).
   *
   * ⚠️ Elle prenait des `sources` — des fichiers — et **personne ne l'a jamais appelée**,
   * parce qu'il n'y a aucun fichier de musique dans le dépôt. La fonction existait, le
   * réglage existait, la persistance existait : seule la musique manquait. C'est le motif
   * de la police Nunito, transposé au son.
   *
   * Il n'y a toujours pas de fichier, et il n'en faut plus : `audio/music.ts` synthétise
   * chaque note à partir de `core/musicPolicy`. Un idle se joue des heures — une boucle
   * de deux minutes entendue soixante fois se fait couper, et l'identité sonore du §4
   * disparaît avec elle.
   */
  registerMusic(): void {
    this.music ??= createMusic();
    if (this.settings.musicEnabled && this.unlocked) this.startMusic();
  }

  /**
   * Relaie le moment de la journée. Même `phase` que `skyAt()` — 0 à minuit.
   *
   * ⚠️ On la RELAIE, on ne la recalcule pas. Deux horloges de cycle dans un même jeu
   * finissent par se croiser, et la musique passerait à l'ambiance de nuit pendant que
   * le ciel est encore bleu.
   */
  setMusicPhase(phase: number): void {
    this.music?.setPhase(phase);
  }

  /**
   * Joue un effet. Silencieux — sans erreur — si le contexte n'est pas débloqué ou si
   * l'échantillon n'est pas encore déclaré : une action de jeu ne doit jamais échouer
   * parce qu'un fichier son manque.
   */
  play(name: SfxName, options: SfxOptions = {}, now: number = performance.now()): void {
    if (!this.unlocked) return;

    const decision = this.limiter.decide(name, now);
    if (!decision.play) return;

    const sample = this.samples.get(name);
    if (sample === undefined) return;

    const id = sample.play();
    const rate = jitterRate(semitoneToRate(options.semitones ?? 0));
    sample.rate(rate, id);
    sample.volume(Math.min(1, this.settings.sfxVolume * decision.gain * (options.gain ?? 1)), id);
  }

  /** Déclare tous les échantillons du MVP d'un coup. Idempotent : rappeler ne
   *  recrée pas les `Howl` déjà chargés. */
  loadDefaultSamples(): void {
    if (this.samples.size > 0) return;
    for (const name of SFX_NAMES) {
      this.register(name, [SFX_SOURCES[name]]);
    }
    /*
      ⚠️ LA MUSIQUE SE DÉCLARE ICI, avec les bruitages — pas ailleurs, pas plus tard.

      `registerMusic()` existait depuis l'étape 4a et **personne ne l'appelait**. Le
      réglage était dans l'écran des préférences, sa persistance marchait, et cocher la
      case ne produisait rien : il n'y avait aucune musique à démarrer. Un an de
      fonctionnalité inerte, sans une erreur nulle part.

      Elle ne coûte rien à déclarer — aucun fichier n'est téléchargé, l'`AudioContext`
      n'est même pas créé tant que le joueur n'a pas fait un geste.
    */
    this.registerMusic();
  }

  /**
   * Son de pose (E§1.1) : impact grave **et** carillon aigu superposé.
   *
   * Seul le carillon est transposé, d'un degré de la gamme à chaque pose consécutive,
   * remis à zéro après trois secondes d'inactivité. Poser cinq bâtiments d'affilée joue
   * une gamme montante ; l'impact, lui, garde toujours la même matière.
   */
  playPlacement(now: number = performance.now()): void {
    this.play('purchase', {}, now);
    this.play('chime', { semitones: this.chime.next(now), gain: 0.7 }, now);
  }

  resetChime(): void {
    this.chime.reset();
  }

  setSettings(next: Partial<SoundSettings>): void {
    this.settings = { ...this.settings, ...next };
    writeSettings(this.settings);

    for (const sample of this.samples.values()) {
      sample.volume(this.settings.sfxVolume);
    }

    if (this.settings.musicEnabled) {
      this.startMusic();
      // Un changement de volume seul ne doit PAS relancer la musique : `setVolume`
      // rampe sur le gain maître, `start` est sans effet si elle joue déjà.
      this.music?.setVolume(this.settings.musicVolume);
    } else {
      this.stopMusic();
    }
  }

  private startMusic(): void {
    const music = this.music;
    if (music === null || !this.unlocked) return;
    music.setVolume(this.settings.musicVolume);
    // Le fondu à l'activation vit dans `music.ts` : chaque note a déjà une attaque
    // lente, donc l'entrée est douce par construction (E§4).
    music.start();
  }

  private stopMusic(): void {
    this.music?.stop();
  }
}

function readSettings(): SoundSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_SETTINGS;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_SETTINGS;

    const record = parsed as Record<string, unknown>;
    return {
      sfxVolume: clampVolume(record.sfxVolume, DEFAULT_SETTINGS.sfxVolume),
      musicVolume: clampVolume(record.musicVolume, DEFAULT_SETTINGS.musicVolume),
      musicEnabled:
        typeof record.musicEnabled === 'boolean'
          ? record.musicEnabled
          : DEFAULT_SETTINGS.musicEnabled,
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function writeSettings(settings: SoundSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Persistance impossible : les réglages valent pour la session, le jeu continue.
  }
}

function clampVolume(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.min(1, Math.max(0, value))
    : fallback;
}
